import {liveSession} from "../../../Engine/LiveSessionManagement.js";
import type {MicroSnoozeComp} from "../../../Engine/LiveSession/Components/MicroSnoozeComp.js";
import type {VisualizationComp} from "../../../Engine/LiveSession/Components/VisualizationComp.js";
import {SessionLog} from "../../../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {LogType} from "../../../UI/Tools/@Shared/LogEntry.js";
import {nativeBridge} from "../Bridge_Native.js";
import {GetRawKeyName} from "../../General/Keys.js";

export let screenOn = true;
nativeBridge.RegisterFunction("ScreenOn", pack=>{
	const {sendTime} = pack as any;
	SessionLog(`Got ScreenOn event! @SendDelay(${Date.now() - sendTime})`, LogType.Event_Large);
	screenOn = true;
	liveSession?.screenStateChangeListeners[0]("on");
});
nativeBridge.RegisterFunction("ScreenOff", pack=>{
	const {sendTime} = pack as any;
	SessionLog(`Got ScreenOff event! @SendDelay(${Date.now() - sendTime})`, LogType.Event_Large);
	screenOn = false;
	liveSession?.screenStateChangeListeners[0]("off");
});

const keysDownNow = {} as {[key: string]: boolean};
nativeBridge.RegisterFunction("NotifyVolumeKeyDown", (volKeyCode: number)=>{
	const keyName = {"-1": "VolumeDown", 1: "VolumeUp"}[volKeyCode]!;
	if (!keysDownNow[keyName]) {
		liveSession?.Log(`Volume key first-downed! VolKeyCode: ${volKeyCode} (${keyName})`);
	}

	keysDownNow[keyName] = true;
	liveSession?.keyDownListeners[0]({key: GetRawKeyName(keyName)} as any);
});
nativeBridge.RegisterFunction("NotifyVolumeKeyUp", (volKeyCode: number)=>{
	const keyName = {"-1": "VolumeDown", 1: "VolumeUp"}[volKeyCode]!;
	const keyWasAlreadyKnownAsUp = !keysDownNow[keyName];
	if (keyWasAlreadyKnownAsUp) {
		// note: this may be bug in OS layer, or limitation of my key-event inference logic (see VolumeProvider.onAdjustVolume in ForegroundService.kt)
		liveSession?.Log(`Volume key upped twice in a row! This shouldn't be possible (OS bug seemingly), so event ignored. VolKeyCode: ${volKeyCode} (${keyName})`);
		return;
	} else {
		liveSession?.Log(`Volume key upped! VolKeyCode: ${volKeyCode} (${keyName})`);
	}

	keysDownNow[keyName] = false;
	liveSession?.keyUpListeners[0]({key: GetRawKeyName(keyName)} as any);
});
nativeBridge.RegisterFunction("NotifyLoudnessHit", (notifyLoudness: number, actualLoudness: number)=>{
	//Remote_SnoozeFail_TryCall(`Calling Remote_SnoozeFail (from NotifyLoudnessHit: ${loudness})`);
	liveSession?.micLoudnessListeners[0](notifyLoudness, actualLoudness);
});

const chunkSizeInMS = 500;
const numberOfChunksInBuffer = 10_000 / chunkSizeInMS;
const motionAveragesOfChunksFromLast10s = [] as number[];
export function ClearMotionAveragesBuffer() {
	motionAveragesOfChunksFromLast10s.Clear();
}
nativeBridge.RegisterFunction("NotifyPhoneMotionSamples", (
	chunkMotionAverage: number,
	/* 0 = phone-back toward earth, 180 = phone-screen toward earth, 90 = halfway between (eg. phone side-edge/top-edge/bottom-edge toward earth) */
	tiltAngle: number
)=>{
	motionAveragesOfChunksFromLast10s.push(chunkMotionAverage);
	if (motionAveragesOfChunksFromLast10s.length > numberOfChunksInBuffer) {
		motionAveragesOfChunksFromLast10s.RemoveAt(0);
	}
	liveSession?.phoneMotionDataListeners[0](motionAveragesOfChunksFromLast10s);

	const microSnoozeComp = liveSession?.Comp_ByString<MicroSnoozeComp>("MicroSnoozeComp");
	if (microSnoozeComp?.behaviorEnabled && microSnoozeComp.triggersEnabled) { // which "enabledness" fields to check is a bit unclear...
		microSnoozeComp.OnTiltAngleReceived(tiltAngle);
	}
});

export const summarySamples_countPerSecond = 16;
export const summarySamples_bufferSize = 60 * summarySamples_countPerSecond;
export const summarySamples_bufferOfLast60s = [] as SummarySample[];
export function ClearSummarySamplesBuffer() {
	summarySamples_bufferOfLast60s.Clear();
}
export class SummarySample {
	constructor(public avg: number, public min: number, public max: number, public absAvg: number, public absMin: number) {}
}
nativeBridge.RegisterFunction("OnMicSampleSet", (summarySamples: SummarySample[])=>{
	summarySamples_bufferOfLast60s.push(...summarySamples);
	if (summarySamples_bufferOfLast60s.length > summarySamples_bufferSize) {
		summarySamples_bufferOfLast60s.splice(0, summarySamples_bufferOfLast60s.length - summarySamples_bufferSize);
	}
	OnMicSampleSet_extraListeners.forEach(a=>a(summarySamples));
});
export const OnMicSampleSet_extraListeners = [] as ((summarySamples: SummarySample[])=>void)[];

export let headphonesConnected: boolean|n = null;
nativeBridge.RegisterFunction("NotifyHeadphonesStatus", (connected: boolean)=>{
	const wasConnected = headphonesConnected;
	headphonesConnected = connected;
	if (wasConnected && !connected) {
		// headphones were connected and are now disconnected; disable force-enabled on visualization comp
		const visComp = liveSession?.Comp_ByString<VisualizationComp>("VisualizationComp");
		if (visComp?.promptingForceEnabled) {
			visComp.StopPromptingForceEnable(visComp.c.forceEnabled_expireSilently);
		}
	}
});